import React, { createContext, useContext, useEffect, useRef, useState, type PropsWithChildren } from 'react';
import { loadSpace } from '@usersnap/browser';
import { useMatch } from 'react-router-dom';

import { userSnapSpaceApiKey } from '../../assets/config';
import { useIsAccountOwner, usePrerenderUser } from '../../hooks/usePrerenderUser';

const ONE_DAY = 24 * 60 * 60 * 1000; // milliseconds in one day
const DELAYED_SURVEY_DELAY_MS = 90 * 1000;

// The integration wizard ends on a rendered snapshot with its own CTAs; a survey
// dialog on top of it would cover them, so no survey is offered on these routes.
// Same pattern the wizard is mounted under in Routes.jsx, so it also matches the
// bare /integration-wizard path.
const INTEGRATION_WIZARD_ROUTE_PATTERN = '/integration-wizard/*';

type UserSnapApi = Awaited<ReturnType<typeof loadSpace>>;
type UserSnapApiProviderProps = PropsWithChildren;

const UserSnapApiContext = createContext<UserSnapApi | null>(null);

export const UserSnapApiProvider = ({ children }: UserSnapApiProviderProps) => {
  const [usersnapApi, setUsersnapApi] = useState<UserSnapApi | null>(null);
  const user = usePrerenderUser();
  const isAccountOwner = useIsAccountOwner();
  const isIntegrationWizardPage = !!useMatch(INTEGRATION_WIZARD_ROUTE_PATTERN);
  // The provider lives at the app root and never remounts, so the surveys are
  // offered once per session. Without this latch, leaving the wizard would
  // re-run the effect and log every event a second time.
  const surveysOffered = useRef(false);

  const loadUserSnapSdk = async () => {
    try {
      const api = await loadSpace(userSnapSpaceApiKey);
      await api.init({
        user: {
          userId: user.id as unknown as string,
          email: user.email,
          plan: user.planName,
          signupDate: user.signupDate?.toISOString(),
        },
      });
      setUsersnapApi(api);
    } catch (error) {
      console.error('Error initializing UserSnap', error);
    }
  };

  useEffect(() => {
    if (!usersnapApi && user.hasSession) {
      loadUserSnapSdk();
    }
  }, [user]);

  useEffect(() => {
    if (
      surveysOffered.current ||
      !usersnapApi ||
      !user.hasSession ||
      !user.trackingCodeInstalledDate ||
      isIntegrationWizardPage
    ) {
      return undefined;
    }

    surveysOffered.current = true;

    if (isAccountOwner) {
      // Event name is the trigger id configured in Usersnap; it no longer implies a delay.
      usersnapApi.logEvent('post-integration-1-week-questionnaire');
    }

    const now = Date.now();
    const daysSinceIntegration = (now - user.trackingCodeInstalledDate.getTime()) / ONE_DAY;
    const daysSinceLastSeen = (now - user.lastSeen.getTime()) / ONE_DAY;
    const daysSinceSignup = (now - user.signupDate.getTime()) / ONE_DAY;

    let delayedSurveyTimeout: ReturnType<typeof setTimeout> | undefined;

    if (daysSinceIntegration > 14 && daysSinceLastSeen < 90) {
      usersnapApi.logEvent('open_UMUX_survey');

      if (daysSinceSignup >= 30) {
        // if access to both, delay the second survey
        delayedSurveyTimeout = setTimeout(() => {
          usersnapApi.logEvent('open_dashboardv1_experience_survey');
        }, DELAYED_SURVEY_DELAY_MS);
      }
    } else if (daysSinceSignup >= 30) {
      usersnapApi.logEvent('open_dashboardv1_experience_survey');
    }

    return () => clearTimeout(delayedSurveyTimeout);
  }, [user.trackingCodeInstalledDate, user.hasSession, usersnapApi, isAccountOwner, isIntegrationWizardPage]);

  return <UserSnapApiContext.Provider value={usersnapApi}>{children}</UserSnapApiContext.Provider>;
};

export const useUserSnap = () => {
  const api = useContext(UserSnapApiContext) as UserSnapApi;

  const openUserSatisfactionWidget = () => api.logEvent('open_user_satisfaction');
  const openIssueTrackingWidget = () => api.logEvent('open_issue_tracking');
  const openChurnQuestionnaire = () => api.logEvent('downgrade_from_paid');
  const openSubmitFeedbackWidget = () => api.logEvent('submit_feedback');

  return {
    api,
    openUserSatisfactionWidget,
    openIssueTrackingWidget,
    openChurnQuestionnaire,
    openSubmitFeedbackWidget,
  };
};
